// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { SelectItem } from '@advisor-app/components';
import { selectAvailableParametersObjectives } from '@advisor-app/store/slices/available-parameters';
import {
  selectObjectiveKey,
  updateOptimizationConfig,
} from '@advisor-app/store/slices/parameters';
import { selectWorkflowAsset } from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { type OptimizationObjective } from '@shared/data-access/interfaces/redux/parameters-state';
import {
  AppIcon,
  SectionContainer,
  useInfoPopup,
  type PopupsName,
} from '@shared/ui-kit/components';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface ObjectiveSelectorProps {
  allowDirectIndexing: boolean;
}

const allowedObjectives = [
  'min_tracking_error',
  'min_volatility',
  'max_diversification_ratio',
  'min_risk_parity',
  'min_neg_sharpe_ratio',
  'equal_weights',
  'market_cap',
];

export const ObjectiveSelector = ({
  allowDirectIndexing,
}: ObjectiveSelectorProps) => {
  const { t } = useTranslation(['ai-builder', 'common', 'components']);
  const dispatch = useDispatch();

  const assetType = useAppSelector(selectWorkflowAsset);
  const rebalanceObjectives = useAppSelector(selectAvailableParametersObjectives);
  const infoPopups = useInfoPopup();

  const objectives =
    rebalanceObjectives[assetType as keyof typeof rebalanceObjectives] ?? [];

  const objectiveKey = useAppSelector(state =>
    selectObjectiveKey(state, 'ai-builder'),
  );

  const displayObjetives = objectives
    .filter(({ key }) => allowedObjectives.includes(key))
    .map(obj => ({
      ...obj,
      name: t(`common.objectives.${obj.key}`, { ns: 'common' }),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const updateObjective = useCallback(
    (objective: OptimizationObjective) => {
      dispatch(updateOptimizationConfig(['ai-builder', { objective }]));
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      assetType !== 'stock' ||
      (!allowDirectIndexing && objectiveKey === 'min_tracking_error')
    ) {
      updateObjective({
        name: t('common.objectives.min_risk_parity', { ns: 'common' }),
        key: 'min_risk_parity',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType, allowDirectIndexing, t]);

  return (
    <SectionContainer
      title={t('ai-builder.select-objective', { ns: 'ai-builder' }) as string}
      variant="h3"
    >
      <div className="boxwrapper grid_4">
        {displayObjetives.map(({ name, key }) =>
          allowDirectIndexing || !(key === 'min_tracking_error') ? (
            <SelectItem
              key={key}
              label={name}
              labelId={key}
              checkedValidator={objectiveKey}
              groupName="objective"
              rightIcon={
                <AppIcon
                  icon="Info"
                  color={objectiveKey === key ? 'light' : 'disabled'}
                  size="md"
                />
              }
              handleSelectItem={() => updateObjective({ name, key })}
              rightIconFunction={() =>
                infoPopups(`ai-builder.${key}` as PopupsName)
              }
              boxTestId={key}
            />
          ) : (
            <SelectItem
              key={key}
              label={name}
              labelId={key}
              groupName="objective"
              rightIcon={
                <AppIcon
                  icon="Info"
                  color={objectiveKey === key ? 'light' : 'disabled'}
                  size="md"
                />
              }
              boxTestId={key}
              disabled
            />
          ),
        )}
      </div>
    </SectionContainer>
  );
};
